// BoardLanesView.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { isEnumCompatibleFieldType, normalizeSchemaFieldType } from 'utilities/FieldTypes';
import {
  LANE_TITLE_COLUMN_KEY,
  resolveLaneColumnWidth,
} from 'views/board/utils/laneColumnWidths';
import { getPeopleProfiles } from 'views/utils/peopleFieldProfiles';
import { getFieldType, isInlineEditable } from 'views/table/utils/tableFieldUtils';

// Hooks
import useLaneColumnSizing from 'views/board/hooks/useLaneColumnSizing';

// CSS
import './BoardLanesView.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import ProfileStack from 'components/profilestack/ProfileStack';
import TableCellEditor from 'views/table/components/TableCellEditor';

/**
 * BoardLanesView
 *
 * ClickUp-style lanes: each group is a header band + its own table (with
 * column headers). Shared horizontal/vertical scroll; sticky Name; titles
 * indented to align with the group color dot.
 *
 * @param {object} props
 * @returns {ReactNode}
 */
const BoardLanesView = ({
  view = null,
  boardData,
  otherKey,
  dragOverTarget,
  draggingNodeKey,
  handleInsertDragOver,
  handleInsertDrop,
  handleCardClick,
  handleCardDoubleClick,
  handleCardDragStart,
  handleCardDragEnd,
  handleCellActivate = null,
  onNodeChange = null,
  editingCell = null,
  editingValue = '',
  setEditingValue = null,
  handleCommitEdit = null,
  handleCancelEdit = null,
  language = 'en',
  typeKey = '',
  cardFieldKeys,
  schemaFieldMap,
  schemaFieldMapLower,
  resolveFieldValue,
  formatFieldValue,
  boardFieldKey,
  nodeDragEnabled,
  addTypeKey,
  addButtonLabel,
  handleAddForColumn,
  isReadOnlyInteraction,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState(() => new Set());

  // ----- MEMOS ----- //

  const fieldKeys = useMemo(() => {
    const keys = Array.isArray(cardFieldKeys) ? cardFieldKeys : [];
    return keys.filter((key) => {
      const normalized = String(key || '').trim();
      if (!normalized) return false;
      if (normalized === LANE_TITLE_COLUMN_KEY) return false;
      if (boardFieldKey && normalized === boardFieldKey) return false;
      return true;
    });
  }, [boardFieldKey, cardFieldKeys]);

  const sizingFieldKeys = useMemo(
    () => [LANE_TITLE_COLUMN_KEY, ...fieldKeys],
    [fieldKeys],
  );

  const resolveFieldDef = useCallback((fieldKey) => (
    schemaFieldMap?.get?.(fieldKey)
    || schemaFieldMapLower?.get?.(String(fieldKey || '').toLowerCase())
    || null
  ), [schemaFieldMap, schemaFieldMapLower]);

  const { effectiveColumnWidths, handleColumnResizeStart } = useLaneColumnSizing({
    view,
    visibleFieldKeys: sizingFieldKeys,
    resolveFieldDef,
  });

  const resolveColumnLabel = useCallback((fieldKey) => {
    if (fieldKey === LANE_TITLE_COLUMN_KEY) return 'Name';
    const field = resolveFieldDef(fieldKey);
    return String(field?.label || fieldKey || '').trim() || fieldKey;
  }, [resolveFieldDef]);

  const columnWidths = useMemo(() => {
    const widths = {
      [LANE_TITLE_COLUMN_KEY]: resolveLaneColumnWidth(
        LANE_TITLE_COLUMN_KEY,
        null,
        effectiveColumnWidths,
      ),
    };
    fieldKeys.forEach((key) => {
      widths[key] = resolveLaneColumnWidth(key, resolveFieldDef(key), effectiveColumnWidths);
    });
    return widths;
  }, [effectiveColumnWidths, fieldKeys, resolveFieldDef]);

  const totalWidth = useMemo(
    () => Object.values(columnWidths).reduce((sum, width) => sum + width, 0),
    [columnWidths],
  );

  const titleWidth = columnWidths[LANE_TITLE_COLUMN_KEY];
  const canResize = !isReadOnlyInteraction && Boolean(view?.key);
  const canEditCells = !isReadOnlyInteraction && typeof handleCellActivate === 'function';
  const canEditEnumPills = !isReadOnlyInteraction && typeof onNodeChange === 'function';

  // ----- HANDLERS ----- //

  const handleFieldCellClick = useCallback((event, node, fieldDef, fieldKey) => {
    if (!canEditCells) return;
    if (isEnumCompatibleFieldType(fieldDef?.type)) return;
    if (editingCell?.nodeKey === node?.key && editingCell?.fieldKey === fieldKey) return;
    event.preventDefault();
    event.stopPropagation();
    handleCellActivate(node, fieldDef, fieldKey);
  }, [canEditCells, editingCell, handleCellActivate]);

  const toggleGroupCollapsed = useCallback((groupKey) => {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  const resolveInsertIndexFromEvent = useCallback((index, event) => {
    if (!event?.currentTarget || typeof index !== 'number') return index;
    const rect = event.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + (rect.height / 2);
    return event.clientY >= midpoint ? index + 1 : index;
  }, []);

  // ----- RENDER HELPERS ----- //

  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: titleWidth }} />
      {fieldKeys.map((key) => (
        <col key={`lane_col_${key}`} style={{ width: columnWidths[key] }} />
      ))}
    </colgroup>
  );

  const renderColumnHeaders = (groupKey) => (
    <tr>
      <th
        className="board-lanes-view-th board-lanes-view-th-sticky board-lanes-view-th-title"
        style={{
          backgroundColor: theme.panel,
          borderBottom: `1px solid ${theme.backgroundHover}`,
          color: theme.foregroundMuted,
          left: 0,
          minWidth: titleWidth,
          width: titleWidth,
        }}
      >
        <div className="board-lanes-view-th-inner">
          <span>Name</span>
          {canResize ? (
            <button
              className="board-lanes-view-resizer"
              type="button"
              aria-label="Resize Name column"
              onMouseDown={(event) => handleColumnResizeStart(event, LANE_TITLE_COLUMN_KEY)}
            />
          ) : null}
        </div>
      </th>
      {fieldKeys.map((fieldKey) => (
        <th
          className="board-lanes-view-th"
          style={{
            backgroundColor: theme.panel,
            borderBottom: `1px solid ${theme.backgroundHover}`,
            color: theme.foregroundMuted,
            minWidth: columnWidths[fieldKey],
            width: columnWidths[fieldKey],
          }}
          key={`lane_th_${groupKey}_${fieldKey}`}
        >
          <div className="board-lanes-view-th-inner">
            <span className="board-lanes-view-th-label">{resolveColumnLabel(fieldKey)}</span>
            {canResize ? (
              <button
                className="board-lanes-view-resizer"
                type="button"
                aria-label={`Resize ${resolveColumnLabel(fieldKey)} column`}
                onMouseDown={(event) => handleColumnResizeStart(event, fieldKey)}
              />
            ) : null}
          </div>
        </th>
      ))}
    </tr>
  );

  // ----- RENDER ----- //

  return (
    <div className="board-lanes-view-container">
      <div className="board-lanes-view-scroll">
        {boardData.columns.map((column) => {
          const items = boardData.groups.get(column.key) || [];
          const isDropLane = column.key !== otherKey;
          const isCollapsed = collapsedGroupKeys.has(column.key);
          const isLaneBodyActiveDrop = isDropLane
            && Boolean(draggingNodeKey)
            && dragOverTarget?.columnKey === column.key
            && dragOverTarget?.insertIndex === items.length;
          const groupBg = theme.backgroundMuted;

          return (
            <section
              className="board-lanes-view-group"
              style={{
                backgroundColor: groupBg,
                minWidth: totalWidth,
                width: totalWidth,
              }}
              key={`lane_group_${column.key}`}
            >

              {/* GROUP HEADER (div band — not a table row) */}
              <div
                className={`board-lanes-view-group-bar${isLaneBodyActiveDrop ? ' is-drop-active' : ''}`}
                style={{
                  borderBottomColor: theme.backgroundHover,
                }}
                onDragOver={(event) => {
                  if (!isDropLane) return;
                  handleInsertDragOver(column.key, items.length, event);
                }}
                onDrop={(event) => {
                  if (!isDropLane) return;
                  handleInsertDrop(column.key, items.length, event);
                }}
              >
                <div
                  className="board-lanes-view-group-sticky"
                  style={{
                    backgroundColor: groupBg,
                  }}
                >
                  <button
                    className="board-lanes-view-collapse"
                    type="button"
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
                    onClick={() => toggleGroupCollapsed(column.key)}
                  >
                    <Icon
                      iconKey={isCollapsed ? 'keyboard_arrow_right' : 'keyboard_arrow_down'}
                      width={18}
                      height={18}
                      //color={column.color || theme.foregroundMuted}
                      color={theme.foregroundMuted}
                    />
                  </button>
                  <div
                    className="board-lanes-view-group-dot"
                    style={{
                      backgroundColor: column.color || 'transparent',
                    }}
                  />
                  <button
                    className="board-lanes-view-group-title"
                    style={{
                      color: theme.foreground,
                    }}
                    type="button"
                    onClick={() => toggleGroupCollapsed(column.key)}
                  >
                    {column.label}
                  </button>
                  <span
                    className="board-lanes-view-group-count"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    {items.length}
                  </span>
                </div>
                {addTypeKey && typeof handleAddForColumn === 'function' ? (
                  <div
                    className="board-lanes-view-group-add"
                    style={{
                      backgroundColor: groupBg,
                    }}
                  >
                    <Button
                      iconKey="add"
                      backgroundColor={theme.panel}
                      foregroundColor={theme.foreground}
                      hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                      hoverForegroundColor={theme.buttonHoverLabelColor}
                      title={addButtonLabel || 'Add'}
                      onClick={() => handleAddForColumn(column.key)}
                      width={36}
                    />
                  </div>
                ) : null}
              </div>

              {/* GROUP TABLE — only when expanded and non-empty */}
              {!isCollapsed && items.length > 0 ? (
                <table
                  className="board-lanes-view-table"
                  style={{
                    width: totalWidth,
                    minWidth: totalWidth,
                  }}
                >
                  {renderColGroup()}
                  <thead>
                    {renderColumnHeaders(column.key)}
                  </thead>
                  <tbody>
                    {items.map((node, index) => {
                      const isDragging = draggingNodeKey && node?.key === draggingNodeKey;
                      const isDropBefore = isDropLane
                        && Boolean(draggingNodeKey)
                        && dragOverTarget?.columnKey === column.key
                        && dragOverTarget?.insertIndex === index;
                      const rowBg = isDragging ? theme.backgroundHover : theme.panel;
                      const isRowEditing = editingCell?.nodeKey === node?.key;

                      return (
                        <tr
                          className={`board-lanes-view-row${isDragging ? ' is-dragging' : ''}${isDropBefore ? ' is-drop-before' : ''}`}
                          style={{
                            backgroundColor: rowBg,
                            borderTopColor: isDropBefore ? theme.foregroundMuted : 'transparent',
                            borderBottomColor: isDropBefore ? theme.red : 'red',
                            color: theme.foreground,
                          }}
                          key={`lane_row_${column.key}_${node?.key || index}`}
                          draggable={nodeDragEnabled && !isRowEditing}
                          onClick={(event) => {
                            if (isRowEditing) return;
                            handleCardClick(event, node);
                          }}
                          onDoubleClick={(event) => {
                            if (isRowEditing) return;
                            handleCardDoubleClick(event, node);
                          }}
                          onDragEnd={handleCardDragEnd}
                          onDragOver={(event) => {
                            if (!isDropLane) return;
                            event.stopPropagation();
                            const insertIndex = resolveInsertIndexFromEvent(index, event);
                            handleInsertDragOver(column.key, insertIndex, event);
                          }}
                          onDragStart={(event) => {
                            if (isRowEditing) {
                              event.preventDefault();
                              return;
                            }
                            handleCardDragStart(node, column.key, event);
                          }}
                          onDrop={(event) => {
                            if (!isDropLane) return;
                            event.stopPropagation();
                            const insertIndex = resolveInsertIndexFromEvent(index, event);
                            handleInsertDrop(column.key, insertIndex, event);
                          }}
                        >
                          <td
                            className="board-lanes-view-td board-lanes-view-td-sticky board-lanes-view-td-title"
                            style={{
                              backgroundColor: rowBg,
                              left: 0,
                              minWidth: titleWidth,
                              width: titleWidth,
                            }}
                          >
                            {String(node?.title || '').trim() || 'Untitled'}
                          </td>
                          {fieldKeys.map((fieldKey) => {
                            const fieldDef = resolveFieldDef(fieldKey);
                            const fieldType = normalizeSchemaFieldType(fieldDef?.type || '');
                            const rawValue = typeof resolveFieldValue === 'function' && fieldDef
                              ? resolveFieldValue(node, fieldDef)
                              : (node?.[fieldKey] ?? null);

                            if (fieldType === 'people') {
                              const peopleProfiles = getPeopleProfiles(rawValue);
                              return (
                                <td
                                  className={`board-lanes-view-td${canEditCells ? ' is-editable' : ''}`}
                                  style={{
                                    backgroundColor: rowBg,
                                    minWidth: columnWidths[fieldKey],
                                    width: columnWidths[fieldKey],
                                  }}
                                  key={`lane_td_${column.key}_${node?.key}_${fieldKey}`}
                                  onClick={(event) => handleFieldCellClick(event, node, fieldDef, fieldKey)}
                                >
                                  {peopleProfiles.length ? (
                                    <div
                                      className="board-lanes-view-people-stack"
                                      title={peopleProfiles
                                        .map((person) => person?.name || '')
                                        .filter(Boolean)
                                        .join(', ')}
                                    >
                                      <ProfileStack
                                        profiles={peopleProfiles}
                                        size={20}
                                        maxDisplay={4}
                                        allowNavigation={false}
                                      />
                                    </div>
                                  ) : '—'}
                                </td>
                              );
                            }

                            if (isEnumCompatibleFieldType(fieldType) && fieldDef) {
                              const enumValue = rawValue == null ? '' : String(rawValue).trim();
                              return (
                                <td
                                  className="board-lanes-view-td board-lanes-view-td-enum"
                                  style={{
                                    backgroundColor: rowBg,
                                    minWidth: columnWidths[fieldKey],
                                    width: columnWidths[fieldKey],
                                  }}
                                  key={`lane_td_${column.key}_${node?.key}_${fieldKey}`}
                                >
                                  <EnumPillSelect
                                    field={fieldDef}
                                    value={enumValue}
                                    node={node}
                                    typeKey={typeKey || node?.type}
                                    canEdit={canEditEnumPills}
                                    onNodeChange={onNodeChange}
                                    placeholder={`${resolveColumnLabel(fieldKey)}…`}
                                    language={language}
                                    fontSize="12px"
                                  />
                                </td>
                              );
                            }

                            const editorFieldType = getFieldType(fieldDef, fieldKey);
                            const isEditing = editingCell?.nodeKey === node?.key
                              && editingCell?.fieldKey === fieldKey;
                            const isInline = Boolean(isInlineEditable(fieldDef, fieldKey));
                            const display = typeof formatFieldValue === 'function'
                              ? formatFieldValue(rawValue, fieldDef, fieldKey)
                              : '';

                            return (
                              <td
                                className={`board-lanes-view-td${canEditCells && (isInline || !isEditing) ? ' is-editable' : ''}${isInline ? ' is-inline' : ''}${isEditing ? ' is-editing' : ''}`}
                                style={{
                                  backgroundColor: rowBg,
                                  minWidth: columnWidths[fieldKey],
                                  width: columnWidths[fieldKey],
                                }}
                                key={`lane_td_${column.key}_${node?.key}_${fieldKey}`}
                                onClick={(event) => handleFieldCellClick(event, node, fieldDef, fieldKey)}
                              >
                                {isEditing ? (
                                  <TableCellEditor
                                    fieldType={editorFieldType}
                                    fieldDef={fieldDef}
                                    value={editingValue}
                                    onCancel={handleCancelEdit}
                                    onChange={setEditingValue}
                                    onCommit={() => handleCommitEdit?.(node, fieldDef, fieldKey)}
                                  />
                                ) : (
                                  display || '—'
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
};

// Dev Tools
BoardLanesView.displayName = 'BoardLanesView';

export default BoardLanesView;
